import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Bike, ShieldCheck, Zap, ChevronDown } from "lucide-react";
import { MaskedLine, Reveal } from "./motion";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG = "/hero-illustration.webp";

const TRUST = [
    { icon: Bike, label: "Gratis antar jemput" },
    { icon: ShieldCheck, label: "Proses higienis" },
    { icon: Zap, label: "Tersedia express" },
];

export default function Hero() {
    const { scrollY } = useScroll();
    const yImg = useTransform(scrollY, [0, 700], [0, 90]);
    const yBlob = useTransform(scrollY, [0, 700], [0, -60]);

    return (
        <section
            id="beranda"
            className="relative flex min-h-[100svh] items-center overflow-hidden pb-16 pt-32 lg:pt-28"
        >
            {/* Background layers */}
            <motion.div
                style={{ y: yBlob }}
                className="pointer-events-none absolute -right-40 -top-24 h-[560px] w-[560px] rounded-full bg-brand-mist blur-3xl"
            />
            <div className="pointer-events-none absolute -left-48 bottom-0 h-[420px] w-[420px] rounded-full bg-brand-mist/70 blur-3xl" />
            <div className="noise-overlay" />

            <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:gap-8">
                {/* Copy */}
                <div className="lg:col-span-7">
                    <MaskedLine delay={0.1}>
                        <p className="mb-6 inline-flex items-center gap-3 rounded-full border border-brand/20 bg-white/70 px-5 py-2 text-xs font-bold uppercase tracking-[0.22em] text-brand backdrop-blur">
                            <span className="h-2 w-2 rounded-full bg-brand" />
                            Laundry Kiloan & Satuan Premium
                        </p>
                    </MaskedLine>

                    <h1 className="font-display text-[clamp(2.6rem,6.4vw,5.4rem)] font-extrabold leading-[1.02] tracking-tight text-ink">
                        <MaskedLine delay={0.2}>Come Do</MaskedLine>
                        <MaskedLine delay={0.35}>
                            <span className="text-brand">Laundry</span>
                        </MaskedLine>
                        <MaskedLine delay={0.5}>With Us.</MaskedLine>
                    </h1>

                    <MaskedLine delay={0.65}>
                        <p className="mt-7 max-w-xl text-base leading-relaxed text-ink/60 sm:text-lg">
                            DigiKlin mengantar dan menjemput cucian Anda —
                            laundry kiloan maupun satuan premium. Cukup pesan
                            lewat website, kurir kami yang urus sisanya.
                        </p>
                    </MaskedLine>

                    <MaskedLine delay={0.78}>
                        <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
                            <button
                                data-testid="hero-cta-primary"
                                onClick={() => scrollToId("form")}
                                className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-deep"
                            >
                                Pesan Antar Jemput
                                <ArrowRight
                                    size={18}
                                    className="transition-transform duration-200 group-hover:translate-x-1"
                                />
                            </button>
                            <button
                                data-testid="hero-cta-secondary"
                                onClick={() => scrollToId("harga")}
                                className="inline-flex items-center justify-center rounded-full border border-ink/10 bg-white/80 px-8 py-4 text-base font-bold text-ink backdrop-blur transition-all duration-200 hover:border-brand hover:text-brand"
                            >
                                Lihat Harga
                            </button>
                        </div>
                    </MaskedLine>

                    <MaskedLine delay={0.9}>
                        <ul className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3">
                            {TRUST.map((t) => (
                                <li
                                    key={t.label}
                                    className="flex items-center gap-2 text-sm font-semibold text-ink/60"
                                >
                                    <t.icon
                                        size={16}
                                        className="text-brand"
                                        strokeWidth={2.4}
                                    />
                                    {t.label}
                                </li>
                            ))}
                        </ul>
                    </MaskedLine>
                </div>

                {/* Visual */}
                <div className="relative lg:col-span-5">
                    <motion.div style={{ y: yImg }} className="relative mx-auto max-w-md">
                        <div className="absolute -inset-6 rounded-[3rem] bg-brand/10 blur-2xl" />
                        <Reveal delay={0.3} y={40}>
                            <div className="relative overflow-hidden rounded-t-[10rem] rounded-b-[2.5rem] border-4 border-white shadow-brand-lg">
                                <img
                                    src={HERO_IMG}
                                    alt="Ilustrasi outlet self-service DigiKlin dengan mesin cuci pintar"
                                    className="aspect-[3/4] w-full object-cover"
                                    loading="eager"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-brand/20 via-transparent to-transparent" />
                            </div>
                        </Reveal>

                        {/* Floating chips */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.1, duration: 0.7 }}
                            className="float-anim absolute -left-3 top-14 hidden items-center gap-3 rounded-2xl border border-white/60 bg-white/80 px-4 py-3 shadow-brand-lg backdrop-blur-xl sm:flex md:-left-10"
                        >
                            <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-mist text-brand">
                                <Bike size={20} />
                            </span>
                            <span>
                                <span className="block text-sm font-bold text-ink">
                                    Gratis Antar Jemput
                                </span>
                                <span className="block text-xs text-ink/50">
                                    Jemput & antar ke pintu Anda
                                </span>
                            </span>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.3, duration: 0.7 }}
                            className="float-anim-slow absolute -right-3 bottom-16 hidden items-center gap-3 rounded-2xl border border-white/60 bg-white/80 px-4 py-3 shadow-brand-lg backdrop-blur-xl sm:flex md:-right-8"
                        >
                            <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-mist text-brand">
                                <ShieldCheck size={20} />
                            </span>
                            <span>
                                <span className="block text-sm font-bold text-ink">
                                    100% Higienis
                                </span>
                                <span className="block text-xs text-ink/50">
                                    Dipisah per Pelanggan
                                </span>
                            </span>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <button
                data-testid="hero-scroll-cue"
                onClick={() => scrollToId("layanan")}
                className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 animate-bounce text-brand lg:block"
                aria-label="Gulir ke bawah"
            >
                <ChevronDown size={28} />
            </button>
        </section>
    );
}
